import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Shield, CheckCircle, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { initializeFirstAdmin } from "@/lib/admin";

export default function AdminSetup() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSetupAdmin = async () => {
    if (!user?.email) {
      setError("You must be logged in to set up admin access");
      return;
    }

    setLoading(true);
    setError("");

    const result = await initializeFirstAdmin(user.email);

    if (result.success) {
      setSuccess(true);
      setTimeout(() => {
        window.location.reload(); // Reload to update admin status
      }, 2000);
    } else {
      setError(result.error || "Failed to set up admin access");
    }

    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center space-y-4">
              <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <div className="text-center">
                <p className="text-xl font-semibold">Admin Setup Complete!</p>
                <p className="text-sm text-muted-foreground mt-1">
                  You now have super admin access. Redirecting...
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Admin Setup</CardTitle>
          <CardDescription>
            Set up the first administrator account for your platform
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2 text-sm text-muted-foreground">
            <p>This will grant super admin privileges to:</p>
            <p className="font-semibold text-foreground">{user?.email}</p>
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              <strong>Important:</strong> This action can only be performed once. 
              The first admin will have full control over the platform.
            </p>
          </div>

          <Button
            onClick={handleSetupAdmin}
            className="w-full"
            disabled={loading}
          >
            {loading ? "Setting up..." : "Initialize Admin Access"}
          </Button>

          <Button
            onClick={() => navigate("/home")}
            variant="outline"
            className="w-full"
          >
            Cancel
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}